import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { flashMessage } from '~/app/actions';
import { pagePublicPath } from '~/lib/pagePublicPath';
import { S, A, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar } from '~/app/ui/Page';
import Tabs from '~/app/ui/Tabs';
import SearchField from '~/app/ui/SearchField';
import EmptyState from '~/app/ui/EmptyState';
import StatusBadge from '~/app/ui/StatusBadge';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import Dialog from '~/app/ui/Dialog';
import { RowMenu } from '~/app/ui/Menu';
import { useQueryParams } from '~/app/ui/useQueryParams';
import CreateWidgetModal from './CreateWidgetModal';
import WidgetDetails, { WIDGET_BODY, WidgetSummary } from './WidgetDetails';
import { buildWidgetSrcDoc } from './widgetPreviewDoc';

const SUMMARIES = gql`
  query getCustomWidgetSummaries {
    customWidgetSummaries(includeInactive: true) {
      _id
      name
      title
      description
      category
      publishedVersion
      isActive
      updatedAt
    }
  }
`;
const USAGE = gql`
  query getCustomWidgetUsage($name: String!) {
    customWidgetUsage(name: $name) {
      name
      pages { pageId path title instances }
    }
  }
`;
const DELETE = gql`mutation DeleteCustomWidget($id: ID!) { deleteCustomWidget(id: $id) { _id isActive } }`;
const RESTORE = gql`mutation RestoreCustomWidget($id: ID!) { restoreCustomWidget(id: $id) { _id isActive } }`;
const DESTROY = gql`mutation PermanentlyDeleteCustomWidget($id: ID!) { permanentlyDeleteCustomWidget(id: $id) }`;

type Usage = { pageId: string; path: string; title?: string | null; instances: number };
type Pending =
  | { kind: 'details' | 'usage' | 'delete' | 'restore' | 'destroy'; widget: WidgetSummary }
  | null;

const useStyles = createUseStyles({
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'box-shadow 120ms ease, border-color 120ms ease',
    '&:hover': { borderColor: '#BFC4CB', boxShadow: '0 4px 14px rgba(23, 26, 32, 0.08)' },
    [DARK]: { '&:hover': { borderColor: S.fieldLine } }
  },
  thumb: {
    position: 'relative',
    height: 180,
    background: S.chrome,
    borderBottom: `1px solid ${S.line}`,
    overflow: 'hidden'
  },
  // The page is drawn at 2.5× the card's width and scaled down, so the thumbnail looks like the section.
  frame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '250%',
    height: '250%',
    border: 0,
    transform: 'scale(0.4)',
    transformOrigin: '0 0',
    pointerEvents: 'none',
    background: '#fff'
  },
  noThumb: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: S.muted, fontSize: 13 },
  open: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    '&:focus-visible': { outline: `2px solid ${S.fg}`, outlineOffset: -2 }
  },
  info: { display: 'grid', gap: 6, padding: '14px 12px 14px 16px' },
  top: { display: 'flex', alignItems: 'flex-start', gap: 8 },
  titles: { flex: 1, minWidth: 0 },
  title: { margin: 0, fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  name: { fontFamily: S.mono, fontSize: 12, color: S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menu: { position: 'relative', zIndex: 2 },
  desc: { margin: 0, fontSize: 13, color: '#4A4D53', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', [DARK]: { color: S.muted } },
  pageList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 },
  pageItem: {
    display: 'grid',
    gap: 2,
    padding: '8px 10px',
    margin: '0 -10px',
    borderRadius: 8,
    textDecoration: 'none',
    color: S.fg,
    '&:hover': { background: S.chrome },
    '&:focus-visible': A.focus,
    '& b': { fontWeight: 600 },
    '& span': { fontFamily: S.mono, fontSize: 12, color: S.muted }
  }
});

/** Renders a card's preview only once the card is on screen. */
const Thumb: React.FC<{ widget: WidgetSummary }> = ({ widget }) => {
  const c = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver((entries) => { if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  const { data, loading } = useQuery(WIDGET_BODY, { variables: { name: widget.name, version: widget.publishedVersion || null }, skip: !visible || !widget.isActive });
  const body = data?.customWidgetForRender;
  return (
    <div className={c.thumb} ref={ref} aria-hidden>
      {body && body.html
        ? <iframe className={c.frame} title={`${widget.title} thumbnail`} sandbox="allow-scripts" tabIndex={-1} srcDoc={buildWidgetSrcDoc(body, `thumb-${widget.name}`, { padding: 0, thumbnail: true })} />
        : <div className={c.noThumb}>{!widget.isActive ? 'Deleted' : (loading || !visible ? '' : 'No preview yet')}</div>}
    </div>
  );
};

const PagesList: React.FC<{ pages: Usage[] }> = ({ pages }) => {
  const c = useStyles();
  return (
    <ul className={c.pageList}>
      {pages.map(p => (
        <li key={p.pageId}>
          <Link className={c.pageItem} to={`/admin/page/${p.pageId}`}>
            <b>{p.title || p.path}{p.instances > 1 ? ` (${p.instances}×)` : ''}</b>
            <span>{pagePublicPath(p.path)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
};

/** Where a widget is live, before deleting it: a live page would lose the section at its next deployment. */
const DeleteWidget: React.FC<{ widget: WidgetSummary; onDelete: () => Promise<void>; onClose: () => void }> = ({ widget, onDelete, onClose }) => {
  const { data, loading, error } = useQuery(USAGE, { variables: { name: widget.name }, fetchPolicy: 'network-only' });
  const pages: Usage[] = data?.customWidgetUsage?.pages || [];
  if (!loading && !error && pages.length > 0) {
    return (
      <Dialog title={`“${widget.title}” is on ${pages.length === 1 ? 'a live page' : `${pages.length} live pages`}`} onClose={onClose} testId="widget-in-use" footer={<Button onClick={onClose}>Close</Button>}>
        <p style={{ marginTop: 0 }}>Deleted widgets don’t render, so these pages would lose the section the next time they’re deployed. Take it off these pages first.</p>
        <PagesList pages={pages} />
      </Dialog>
    );
  }
  return (
    <ConfirmDialog
      danger
      title={`Delete “${widget.title}”?`}
      confirmLabel="Delete widget"
      confirmDisabled={loading}
      onConfirm={onDelete}
      onClose={onClose}
      testId="widget-delete"
    >
      <p>
        {loading ? 'Checking the live pages…'
          : error ? `Couldn’t check where it’s used (${error.message}). Pages that use it would lose the section at their next deployment.`
            : 'No live page uses it.'}
        {' '}It moves to Deleted, where you can restore it.
      </p>
    </ConfirmDialog>
  );
};

const UsageDialog: React.FC<{ widget: WidgetSummary; onClose: () => void }> = ({ widget, onClose }) => {
  const { data, loading, error } = useQuery(USAGE, { variables: { name: widget.name }, fetchPolicy: 'network-only' });
  const pages: Usage[] = data?.customWidgetUsage?.pages || [];
  return (
    <Dialog title={`Where “${widget.title}” is live`} onClose={onClose} testId="widget-usage">
      {loading && <p>Checking the live pages…</p>}
      {error && <p>Couldn’t check: {error.message}</p>}
      {!loading && !error && pages.length === 0 && <p>No live page uses it. Drafts aren’t counted.</p>}
      {pages.length > 0 && <><p style={{ marginTop: 0 }}>Live versions of these pages use it.</p><PagesList pages={pages} /></>}
    </Dialog>
  );
};

const WidgetsScreen: React.FC = () => {
  const c = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  // Deleting forever is super admin only on the server (permanentlyDeleteCustomWidget).
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const [params] = useQueryParams();
  const deleted = params.get('view') === 'deleted';
  const { data, loading, error, refetch } = useQuery(SUMMARIES, { fetchPolicy: 'cache-and-network' });
  const opts = { refetchQueries: ['getCustomWidgetSummaries'], awaitRefetchQueries: true };
  const [del] = useMutation(DELETE, opts);
  const [restore] = useMutation(RESTORE, opts);
  const [destroy] = useMutation(DESTROY, opts);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  const all: WidgetSummary[] = useMemo(() => data?.customWidgetSummaries || [], [data]);
  const active = all.filter(w => w.isActive);
  const gone = all.filter(w => !w.isActive);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (deleted ? gone : active).filter(w => !needle || [w.title, w.name, w.description].some(x => String(x || '').toLowerCase().includes(needle)));
  }, [deleted, gone, active, q]);

  const openBuilder = (w: WidgetSummary) => history.push(`/admin/widgets/builder/${w._id}`);

  const menuFor = (w: WidgetSummary) => (w.isActive ? [
    [
      { key: 'open', label: 'Open in builder', icon: 'edit', to: `/admin/widgets/builder/${w._id}` },
      { key: 'details', label: 'Preview and code', icon: 'visibility', onSelect: () => setPending({ kind: 'details', widget: w }) },
      { key: 'usage', label: 'Where it’s live', icon: 'description', onSelect: () => setPending({ kind: 'usage', widget: w }) }
    ],
    [{ key: 'delete', label: 'Delete…', icon: 'delete_outline', danger: true, onSelect: () => setPending({ kind: 'delete', widget: w }) }]
  ] : [
    [{ key: 'restore', label: 'Restore', icon: 'restore', onSelect: () => setPending({ kind: 'restore', widget: w }) }],
    isSuperAdmin ? [{ key: 'destroy', label: 'Delete forever…', icon: 'delete_forever', danger: true, onSelect: () => setPending({ kind: 'destroy', widget: w }) }] : []
  ]);

  let body: React.ReactNode;
  if (error && !data) body = <EmptyState tone="error" title="Couldn’t load widgets" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  else if (loading && !data) body = <EmptyState icon="hourglass_empty" title="Loading widgets…" compact />;
  else if (shown.length === 0) {
    body = q
      ? <EmptyState icon="search_off" title={`No widgets match “${q}”`} action={<Button onClick={() => setQ('')}>Clear search</Button>} />
      : deleted
        ? <EmptyState icon="delete_outline" title="Nothing deleted">Deleted widgets wait here and can be restored.</EmptyState>
        : <EmptyState icon="widgets" title="No custom widgets yet" action={<Button variant="primary" icon="add" onClick={() => setCreating(true)}>New widget</Button>}>Build a section from code, or paste one from your AI tool.</EmptyState>;
  } else {
    body = (
      <div className={c.grid} data-testid="widget-grid">
        {shown.map(w => (
          <article key={w._id} className={c.card} data-widget={w.name}>
            <Thumb widget={w} />
            {w.isActive && (
              <Link className={c.open} to={`/admin/widgets/builder/${w._id}`} aria-label={`Open ${w.title} in the builder`} />
            )}
            <div className={c.info}>
              <div className={c.top}>
                <div className={c.titles}>
                  <h3 className={c.title} title={w.title}>{w.title}</h3>
                  <div className={c.name}>Custom_{w.name}</div>
                </div>
                <div className={c.menu}>
                  <RowMenu label={`Actions for ${w.title}`} groups={menuFor(w)} testId={`widget-menu-${w.name}`} />
                </div>
              </div>
              <div>
                {!w.isActive ? <StatusBadge tone="gray" label="Deleted" />
                  : w.publishedVersion ? <StatusBadge tone="green" label={`Published v${w.publishedVersion}`} />
                    : <StatusBadge tone="amber" label="Not published" title="Pages can’t use it until a version is published" />}
              </div>
              {w.description && <p className={c.desc}>{w.description}</p>}
            </div>
          </article>
        ))}
      </div>
    );
  }

  const done = (message: string) => dispatch(flashMessage(message));
  const dialog = (() => {
    if (!pending) return null;
    const { kind, widget: w } = pending;
    const close = () => setPending(null);
    if (kind === 'details') return <WidgetDetails widget={w} onClose={close} onOpen={() => openBuilder(w)} />;
    if (kind === 'usage') return <UsageDialog widget={w} onClose={close} />;
    if (kind === 'delete') {
      return <DeleteWidget widget={w} onClose={close} onDelete={async () => { await del({ variables: { id: w._id } }); done(`Deleted “${w.title}”. It’s under Deleted if you need it back.`); }} />;
    }
    if (kind === 'restore') {
      return (
        <ConfirmDialog title={`Restore “${w.title}”?`} confirmLabel="Restore" onClose={close}
          onConfirm={async () => { await restore({ variables: { id: w._id } }); done(`Restored “${w.title}”.`); }}>
          <p>It goes back to the active widgets, and pages that use it render it again when they’re next deployed.</p>
        </ConfirmDialog>
      );
    }
    return (
      <ConfirmDialog danger title={`Delete “${w.title}” forever?`} confirmLabel="Delete forever" onClose={close}
        onConfirm={async () => { await destroy({ variables: { id: w._id } }); done(`Deleted “${w.title}” forever.`); }}>
        <p>Its code and every version are removed. This can’t be undone.</p>
      </ConfirmDialog>
    );
  })();

  return (
    <Page testId="widgets-screen">
      <PageHeader
        title={tr('Custom widgets', '自定义组件')}
        description="Sections built from code. Editors add them to pages from the Insert panel; pages use the published version."
        actions={<Button variant="primary" icon="add" onClick={() => setCreating(true)} data-testid="new-widget">New widget</Button>}
      />
      <Tabs
        label="Widgets"
        items={[
          { key: 'active', label: 'Active', to: '/admin/widgets', count: data ? active.length : null, active: !deleted, testId: 'tab-active' },
          { key: 'deleted', label: 'Deleted', to: '/admin/widgets?view=deleted', count: data ? gone.length : null, active: deleted, testId: 'tab-deleted' }
        ]}
      />
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder="Search widgets" testId="widgets-search" />
      </Toolbar>
      {body}
      {creating && (
        <CreateWidgetModal
          onClose={() => setCreating(false)}
          onSuccess={(w: { _id: string }) => { setCreating(false); history.push(`/admin/widgets/builder/${w._id}`); }}
        />
      )}
      {dialog}
    </Page>
  );
};

export default WidgetsScreen;
